import React, { useMemo, useState } from "react"
import { Timeline, Typography, Switch, Empty } from "antd"
import { GoogleMaps } from "../GoogleMaps/index"
import {
	ExternalDeliveryStatus,
	getExternalStatusLabel,
	getExternalStatusColorHex,
	isReducedExternalStatus,
} from "utils/status"
import Strings from "utils/strings"

const { Text } = Typography

// ---------------------------------------------------------------------------
// Public contract — kept self-contained so this component can be reused across
// the app without coupling to any screen's types. The Routes `StatusFlowEvent`
// shape is structurally compatible with `ExternalFlowEntry`.
// ---------------------------------------------------------------------------

export interface MapLatLng {
	lat: number
	lng: number
}

export interface ExternalFlowEntry {
	status: ExternalDeliveryStatus
	timestamp: { $date: string } | string
	location?: MapLatLng | null
}

export interface DeliveryStatusMapProps {
	/** Planned delivery location (the address). Rendered as the "P" marker. */
	plannedLocation?: MapLatLng | null
	/** Actual delivered location, rendered as the "D" marker when present. */
	deliveredLocation?: MapLatLng | null
	/** External partner status flow (located per event). */
	externalFlow?: ExternalFlowEntry[]
	/**
	 * When true, render the external located timeline + map. When false, render
	 * a full-width map with just planned + actual delivery markers.
	 *
	 * Internal deliveries only carry a location at the final delivered point, so
	 * the internal view intentionally has no located timeline.
	 */
	isExternal?: boolean
	/** Initial state of the reduce toggle — show only key statuses. Default true. */
	reduceStatus?: boolean
	/** Whether to render the "show all statuses" toggle (external only). Default true. */
	showReduceToggle?: boolean
	/** Map height (px or CSS string). Default 300. */
	mapHeight?: number | string
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

interface UnifiedEvent {
	label: string
	colorHex: string
	date: Date
	location?: MapLatLng | null
	/** Whether this event belongs to the reduced "key statuses" set. */
	kept: boolean
}

function toDate(value: { $date: string } | string): Date {
	return new Date(typeof value === "string" ? value : value.$date)
}

function formatTimestamp(d: Date): string {
	const time = d.toLocaleTimeString(undefined, {
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	})
	const date = d.toLocaleDateString(undefined, {
		day: "2-digit",
		month: "2-digit",
	})
	return `${time} · ${date}`
}

function isValidLocation(loc?: MapLatLng | null): loc is MapLatLng {
	return !!loc && typeof loc.lat === "number" && typeof loc.lng === "number"
}

const PLANNED_COLOR = "#1890ff"
const DELIVERED_COLOR = "#52c41a"

const LegendDot: React.FC<{ color: string; label: string }> = ({
	color,
	label,
}) => (
	<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
		<div
			style={{
				width: 12,
				height: 12,
				borderRadius: "50%",
				backgroundColor: color,
				border: "2px solid white",
				boxShadow: "0 0 2px rgba(0,0,0,0.3)",
			}}
		/>
		<Text type="secondary">{label}</Text>
	</div>
)

const DeliveryStatusMap: React.FC<DeliveryStatusMapProps> = ({
	plannedLocation,
	deliveredLocation,
	externalFlow = [],
	isExternal = false,
	reduceStatus = true,
	showReduceToggle = true,
	mapHeight = 300,
}) => {
	const [reduced, setReduced] = useState(reduceStatus)

	// External located timeline. Internal deliveries have no per-event location
	// (only the final delivered point), so they don't produce timeline events.
	const allEvents = useMemo<UnifiedEvent[]>(() => {
		if (!isExternal) return []
		return externalFlow
			.map((e) => ({
				label: getExternalStatusLabel(e.status),
				colorHex: getExternalStatusColorHex(e.status),
				date: toDate(e.timestamp),
				location: e.location,
				kept: isReducedExternalStatus(e.status),
			}))
			.sort((a, b) => a.date.getTime() - b.date.getTime())
	}, [isExternal, externalFlow])

	const hasHiddenEvents = useMemo(
		() => allEvents.some((e) => !e.kept),
		[allEvents]
	)

	const visibleEvents = useMemo(
		() => (reduced ? allEvents.filter((e) => e.kept) : allEvents),
		[reduced, allEvents]
	)

	// Located events get a sequential number shared between timeline and map.
	const numberedEvents = useMemo(() => {
		let n = 0
		return visibleEvents.map((event) => {
			const hasLoc = isValidLocation(event.location)
			return { event, markerNumber: hasLoc ? ++n : null }
		})
	}, [visibleEvents])

	const markers = useMemo(() => {
		const result: any[] = []

		if (isValidLocation(plannedLocation)) {
			result.push({
				lat: plannedLocation.lat,
				lng: plannedLocation.lng,
				label: {
					text: "P",
					color: "white",
					fontSize: "14px",
					fontWeight: "bold",
				},
				icon: {
					path: 0,
					fillColor: PLANNED_COLOR,
					fillOpacity: 1,
					scale: 14,
					strokeColor: "white",
					strokeWeight: 2,
				},
			})
		}

		// External located events (none for internal — numberedEvents is empty).
		for (const { event, markerNumber } of numberedEvents) {
			if (markerNumber == null || !isValidLocation(event.location)) continue
			result.push({
				lat: event.location.lat,
				lng: event.location.lng,
				label: {
					text: String(markerNumber),
					color: "white",
					fontSize: "11px",
					fontWeight: "bold",
				},
				icon: {
					path: 0,
					fillColor: event.colorHex,
					fillOpacity: 1,
					scale: 12,
					strokeColor: "white",
					strokeWeight: 2,
				},
			})
		}

		if (isValidLocation(deliveredLocation)) {
			result.push({
				lat: deliveredLocation.lat,
				lng: deliveredLocation.lng,
				label: {
					text: "D",
					color: "white",
					fontSize: "14px",
					fontWeight: "bold",
				},
				icon: {
					path: 0,
					fillColor: DELIVERED_COLOR,
					fillOpacity: 1,
					scale: 14,
					strokeColor: "white",
					strokeWeight: 2,
				},
			})
		}

		return result
	}, [plannedLocation, deliveredLocation, numberedEvents])

	const boundingBox = useMemo(() => {
		if (markers.length < 2) return undefined
		const lats = markers.map((m) => m.lat)
		const lngs = markers.map((m) => m.lng)
		const pad = 0.003
		return {
			south: Math.min(...lats) - pad,
			north: Math.max(...lats) + pad,
			west: Math.min(...lngs) - pad,
			east: Math.max(...lngs) + pad,
		}
	}, [markers])

	const center = useMemo(
		() =>
			markers.length > 0
				? { lat: markers[0].lat, lng: markers[0].lng }
				: undefined,
		[markers]
	)

	const containerStyle: React.CSSProperties = {
		width: "100%",
		height: typeof mapHeight === "number" ? `${mapHeight}px` : mapHeight,
		borderRadius: 5,
	}

	const hasMap = markers.length > 0
	const hasTimeline = visibleEvents.length > 0
	const hasDelivered = isValidLocation(deliveredLocation)

	const mapElement = (
		<GoogleMaps
			{...(boundingBox ? { boundingBox } : { zoom: 15, center })}
			mapContainerStyle={containerStyle}
			markers={markers}
			options={{
				gestureHandling: "cooperative",
				clickableIcons: false,
			}}
		/>
	)

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{/* Header: source label + reduce toggle (external only) */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 8,
					flexWrap: "wrap",
				}}
			>
				<Text strong>
					{isExternal
						? Strings.routes.deliveryProgressExternal
						: Strings.routes.deliveryProgressInternal}
				</Text>
				{isExternal && showReduceToggle && hasHiddenEvents && (
					<span style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{reduced
								? Strings.routes.showKeyStatuses
								: Strings.routes.showAllStatuses}
						</Text>
						<Switch
							size="small"
							checked={!reduced}
							onChange={(checked) => setReduced(!checked)}
						/>
					</span>
				)}
			</div>

			{isExternal ? (
				// External: located timeline + map side by side.
				!hasMap && !hasTimeline ? (
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description={Strings.routes.deliveryNoStatusEvents}
					/>
				) : (
					<div
						style={{
							display: "flex",
							gap: 12,
							alignItems: "stretch",
							minHeight: 200,
							flexWrap: "wrap",
						}}
					>
						<div style={{ flex: "0 0 220px", minWidth: 200 }}>
							{hasTimeline ? (
								<Timeline style={{ marginTop: 4 }}>
									{numberedEvents.map(({ event, markerNumber }, index) => (
										<Timeline.Item key={index} color={event.colorHex}>
											<Text strong style={{ fontSize: 13 }}>
												{markerNumber != null ? `${markerNumber}. ` : ""}
												{event.label}
											</Text>
											<br />
											<Text type="secondary" style={{ fontSize: 12 }}>
												{formatTimestamp(event.date)}
											</Text>
										</Timeline.Item>
									))}
								</Timeline>
							) : (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description={Strings.routes.deliveryNoStatusEvents}
								/>
							)}
						</div>

						<div style={{ flex: 1, minWidth: 240 }}>
							{hasMap ? (
								mapElement
							) : (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description={Strings.routes.deliveryNoLocationData}
								/>
							)}
						</div>
					</div>
				)
			) : // Internal: full-width map with planned + actual markers only.
			hasMap ? (
				<>
					{mapElement}
					<div style={{ marginTop: 8, fontSize: 12 }}>
						<div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
							<LegendDot
								color={PLANNED_COLOR}
								label={Strings.routes.plannedLocation}
							/>
							{hasDelivered && (
								<LegendDot
									color={DELIVERED_COLOR}
									label={Strings.routes.actualDeliveryLocation}
								/>
							)}
						</div>
					</div>
				</>
			) : (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={Strings.routes.deliveryNoLocationData}
				/>
			)}
		</div>
	)
}

export default DeliveryStatusMap
